import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { MSP as MockMSP } from '@/data/mockData';

// Note: email and telefoon are excluded from public views for security
// Contact info is only available to authenticated users via get_msp_contact_info function
export interface DBMsp {
  id: string;
  naam: string;
  slug: string;
  logo_url: string | null;
  plaats: string;
  provincie: string;
  land: string | null;
  aantal_medewerkers: number | null;
  doelgroep_segment: string[] | null;
  branches: string[] | null;
  diensten: string[] | null;
  certificeringen: string[] | null;
  nis2_ondersteuning: 'geen' | 'basis' | 'volledig' | null;
  nis2_beschrijving: string | null;
  qm_certificeringen: string[] | null;
  msp_model: string | null;
  prijsniveau: string | null;
  prijsindicatie: 'laag' | 'midden' | 'hoog' | null;
  korte_pitch: string | null;
  uitgebreide_omschrijving: string | null;
  techstack: string | null;
  klantcases: string | null;
  /** @deprecated legacy field — no longer used for public ranking */
  is_betaald_profiel?: boolean | null;
  /** @deprecated legacy field — no longer used for public ranking */
  prominente_positie?: boolean | null;
  actief: boolean | null;
  website: string | null;
  email?: string | null;
  telefoon?: string | null;
  usps: string[] | null;
  opening_hours?: unknown;
  is_24_7_soc?: boolean | null;
  kvk_verified?: boolean | null;
  kvk_nummer?: string | null;
  oprichtingsjaar?: number | null;
  response_time?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  external_reviews?: unknown;

  nis2_werkplek_identity?: boolean | null;
  nis2_cloud_infra?: boolean | null;
  nis2_support_incident?: boolean | null;
  nis2_backup_bcdr?: boolean | null;
  nis2_patch_vulnerability?: boolean | null;
  nis2_network_access?: boolean | null;
  nis2_detection_monitoring?: boolean | null;
  nis2_security_awareness?: boolean | null;

  nis2_incident_procedure_documented?: boolean | null;
  nis2_incident_procedure_tested?: boolean | null;
  nis2_vulnerabilities_prioritized?: boolean | null;
  nis2_network_segmentation?: boolean | null;
  nis2_logging_centralized?: boolean | null;
  nis2_alerts_followed_up?: boolean | null;
  nis2_training_yearly?: boolean | null;
  nis2_results_measured?: boolean | null;

  extra_it_advies_roadmap?: boolean | null;
  extra_applicatiebeheer?: boolean | null;
  extra_licentiebeheer?: boolean | null;
  extra_hardware_lifecycle?: boolean | null;
  extra_onsite_support?: boolean | null;
  extra_projecten_migraties?: boolean | null;

  nis2_flags_confirmed_at?: string | null;
  calculated_sc_level?: 'SC10' | 'SC20' | 'SC30' | null;
  uses_legacy_inference?: boolean;
  nis2_calculated_level?: string | null;

  engagement_models?: string[] | null;
  support_hours?: string | null;
  response_time_tier?: string | null;
  minimum_contract_duration?: string | null;
  verification_status?: 'unverified' | 'verified' | 'declined';

  // Admin-only test flags — only present via the admin merge path.
  is_test_account?: boolean | null;
  show_in_matching_for_admin_testing?: boolean | null;

  // Promotion / visibility fields (from msps_public_ranked). NULL for admin-test rows.
  promotion_tier?: number | null;
  promotion_reason?: string | null;
  homepage_featured_priority?: number | null;
  plan_label?: string | null;
}

export interface MSPWithRating extends DBMsp {
  average_rating: number;
  review_count: number;
}

function normalizePromotionTier(v: unknown): 300 | 200 | 100 | 0 {
  if (v === 300 || v === 200 || v === 100 || v === 0) return v;
  return 0;
}
function normalizePromotionReason(v: unknown):
  | 'plus_active' | 'basis_active' | 'lead_billing_active' | 'baseline' {
  if (v === 'plus_active' || v === 'basis_active' || v === 'lead_billing_active') return v;
  return 'baseline';
}
function normalizePlanLabel(v: unknown): 'Plus' | 'Basis' | null {
  if (v === 'Plus' || v === 'Basis') return v;
  return null;
}

// Convert DB MSP to a format compatible with existing components
export function convertToMSP(dbMsp: DBMsp | MSPWithRating): MockMSP {
  return {
    // @ts-expect-error - is_test_account is passed through for tracking and not part of MockMSP type
    is_test_account: (dbMsp as any).is_test_account ?? false,
    id: dbMsp.id,
    naam: dbMsp.naam,
    slug: dbMsp.slug,
    logo_url: dbMsp.logo_url ?? '/placeholder.svg',
    plaats: dbMsp.plaats,
    provincie: dbMsp.provincie,
    land: dbMsp.land ?? 'NL',
    aantal_medewerkers: dbMsp.aantal_medewerkers ?? 0,
    doelgroep_segment: dbMsp.doelgroep_segment ?? [],
    branches: dbMsp.branches ?? [],
    diensten: dbMsp.diensten ?? [],
    certificeringen: dbMsp.certificeringen ?? [],
    nis2_ondersteuning: dbMsp.nis2_ondersteuning ?? 'geen',
    nis2_beschrijving: dbMsp.nis2_beschrijving ?? undefined,
    qm_certificeringen: dbMsp.qm_certificeringen ?? [],
    msp_model: (dbMsp.msp_model as 'abonnement' | 'uurtarief' | 'gecombineerd' | 'op_maat') ?? 'abonnement',
    prijsniveau: (dbMsp.prijsniveau as 'instap' | 'regulier' | 'premium') ?? 'regulier',
    korte_pitch: dbMsp.korte_pitch ?? '',
    uitgebreide_omschrijving: dbMsp.uitgebreide_omschrijving ?? '',
    techstack: dbMsp.techstack ?? '',
    klantcases: dbMsp.klantcases ?? '',
    is_betaald_profiel: dbMsp.is_betaald_profiel ?? false,
    prominente_positie: dbMsp.prominente_positie ?? false,
    actief: dbMsp.actief ?? true,
    website: dbMsp.website ?? undefined,
    email: dbMsp.email ?? undefined,
    telefoon: dbMsp.telefoon ?? undefined,
    usps: dbMsp.usps ?? [],
    average_rating: 'average_rating' in dbMsp ? (dbMsp as MSPWithRating).average_rating : 0,
    review_count: 'review_count' in dbMsp ? (dbMsp as MSPWithRating).review_count : 0,
    opening_hours: dbMsp.opening_hours as Record<string, { open: string; close: string }> ?? undefined,
    kvk_verified: dbMsp.kvk_verified ?? false,
    kvk_nummer: dbMsp.kvk_nummer ?? undefined,
    oprichtingsjaar: dbMsp.oprichtingsjaar ?? undefined,
    response_time: dbMsp.response_time as MockMSP['response_time'] ?? undefined,
    latitude: dbMsp.latitude ?? undefined,
    longitude: dbMsp.longitude ?? undefined,

    calculated_sc_level: dbMsp.calculated_sc_level ?? null,
    uses_legacy_inference: dbMsp.uses_legacy_inference ?? true,
    nis2_flags_confirmed_at: dbMsp.nis2_flags_confirmed_at ?? undefined,

    nis2_werkplek_identity: dbMsp.nis2_werkplek_identity ?? false,
    nis2_cloud_infra: dbMsp.nis2_cloud_infra ?? false,
    nis2_support_incident: dbMsp.nis2_support_incident ?? false,
    nis2_backup_bcdr: dbMsp.nis2_backup_bcdr ?? false,
    nis2_patch_vulnerability: dbMsp.nis2_patch_vulnerability ?? false,
    nis2_network_access: dbMsp.nis2_network_access ?? false,
    nis2_detection_monitoring: dbMsp.nis2_detection_monitoring ?? false,
    nis2_security_awareness: dbMsp.nis2_security_awareness ?? false,

    nis2_incident_procedure_documented: dbMsp.nis2_incident_procedure_documented ?? false,
    nis2_incident_procedure_tested: dbMsp.nis2_incident_procedure_tested ?? false,
    nis2_vulnerabilities_prioritized: dbMsp.nis2_vulnerabilities_prioritized ?? false,
    nis2_network_segmentation: dbMsp.nis2_network_segmentation ?? false,
    nis2_logging_centralized: dbMsp.nis2_logging_centralized ?? false,
    nis2_alerts_followed_up: dbMsp.nis2_alerts_followed_up ?? false,
    nis2_training_yearly: dbMsp.nis2_training_yearly ?? false,
    nis2_results_measured: dbMsp.nis2_results_measured ?? false,

    is_24_7_soc: dbMsp.is_24_7_soc ?? false,

    engagement_models: (dbMsp as any).engagement_models ?? [],
    support_hours: (dbMsp as any).support_hours ?? 'business_hours',
    response_time_tier: (dbMsp as any).response_time_tier ?? 'next_business_day',
    minimum_contract_duration: (dbMsp as any).minimum_contract_duration ?? undefined,
    verification_status: (dbMsp as any).verification_status ?? 'unverified',

    // Promotion / visibility (safe defaults for admin-test rows)
    promotion_tier: normalizePromotionTier((dbMsp as any).promotion_tier),
    promotion_reason: normalizePromotionReason((dbMsp as any).promotion_reason),
    homepage_featured_priority:
      typeof (dbMsp as any).homepage_featured_priority === 'number'
        ? (dbMsp as any).homepage_featured_priority
        : null,
    plan_label: normalizePlanLabel((dbMsp as any).plan_label),
  };
}

// Columns fetched from public ranked view (test accounts already excluded server-side).
const PUBLIC_RANKED_COLUMNS = [
  'id', 'naam', 'slug', 'logo_url', 'plaats', 'provincie', 'land',
  'aantal_medewerkers', 'doelgroep_segment', 'branches', 'diensten',
  'certificeringen', 'qm_certificeringen',
  'nis2_ondersteuning', 'calculated_sc_level',
  'msp_model', 'prijsniveau', 'prijsindicatie',
  'korte_pitch', 'uitgebreide_omschrijving', 'techstack', 'klantcases',
  'website', 'usps', 'oprichtingsjaar',
  'response_time', 'response_time_tier', 'support_hours',
  'engagement_models', 'minimum_contract_duration',
  'is_publicly_listed', 'is_24_7_soc', 'verification_status',
  'latitude', 'longitude',
  'average_rating', 'review_count',
  'nis2_flags_confirmed_at', 'uses_legacy_inference',
  'nis2_werkplek_identity', 'nis2_cloud_infra', 'nis2_support_incident',
  'nis2_backup_bcdr', 'nis2_patch_vulnerability', 'nis2_network_access',
  'nis2_detection_monitoring', 'nis2_security_awareness',
  'nis2_incident_procedure_documented', 'nis2_incident_procedure_tested',
  'nis2_vulnerabilities_prioritized', 'nis2_network_segmentation',
  'nis2_logging_centralized', 'nis2_alerts_followed_up',
  'nis2_training_yearly', 'nis2_results_measured',
  // Promotion / visibility
  'promotion_tier', 'promotion_reason', 'homepage_featured_priority', 'plan_label',
].join(',');

// Admin-only test-account merge path uses the older view that still carries the flags.
const ADMIN_TEST_COLUMNS = PUBLIC_RANKED_COLUMNS
  .split(',')
  .filter((c) => c !== 'promotion_tier' && c !== 'promotion_reason'
    && c !== 'homepage_featured_priority' && c !== 'plan_label')
  .concat(['is_test_account', 'show_in_matching_for_admin_testing'])
  .join(',');

async function fetchPublicRankedMSPs(): Promise<MSPWithRating[]> {
  const { data, error } = await supabase
    .from('msps_public_ranked' as any)
    .select(PUBLIC_RANKED_COLUMNS)
    .eq('is_publicly_listed', true);
  if (error) throw error;
  return (data as unknown as MSPWithRating[]) || [];
}

async function fetchAdminTestMSPs(): Promise<MSPWithRating[]> {
  const { data, error } = await supabase
    .from('msps_public_with_ratings' as any)
    .select(ADMIN_TEST_COLUMNS)
    .eq('is_test_account', true)
    .eq('show_in_matching_for_admin_testing', true);
  if (error) throw error;
  return (data as unknown as MSPWithRating[]) || [];
}

async function fetchMSPsList(includeAdminTestMsps: boolean): Promise<MSPWithRating[]> {
  if (!includeAdminTestMsps) {
    return fetchPublicRankedMSPs();
  }
  const [publicRows, testRows] = await Promise.all([
    fetchPublicRankedMSPs(),
    fetchAdminTestMSPs(),
  ]);
  // Defensive: ensure test rows carry baseline promotion metadata and are flagged.
  const normalizedTest = testRows.map((row) => ({
    ...row,
    promotion_tier: 0,
    promotion_reason: 'baseline',
    homepage_featured_priority: null,
    plan_label: null,
    is_test_account: true,
  }));
  // Merge + dedupe by id (public rows win).
  const byId = new Map<string, MSPWithRating>();
  for (const r of publicRows) byId.set(r.id, r);
  for (const r of normalizedTest) {
    if (!byId.has(r.id)) byId.set(r.id, r as MSPWithRating);
  }
  return Array.from(byId.values());
}

export function useMSPs(options?: { enabled?: boolean }) {
  const { isAdmin, isSuperAdmin } = useAuth();
  const includeAdminTestMsps = isAdmin || isSuperAdmin;
  const enabled = options?.enabled ?? true;

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    // v2 = ranked view + admin-test merge (invalidates old caches from is_betaald_profiel era)
    queryKey: ['msps', 'ranked-v2', includeAdminTestMsps ? 'admin' : 'public'],
    queryFn: () => fetchMSPsList(includeAdminTestMsps),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    enabled,
  });

  const msps = useMemo(() => data ?? [], [data]);
  const convertedMSPs = useMemo(() => msps.map(convertToMSP), [msps]);

  return {
    msps: convertedMSPs,
    rawMsps: msps,
    // While the query is disabled react-query reports isLoading=false; treat a
    // not-yet-started fetch as loading so consumers never show an empty state.
    loading: isLoading || isFetching || (enabled && !data && !error),
    error: error ? (error instanceof Error ? error.message : 'Er is een fout opgetreden') : null,
    refetch,
  };
}

export function useMSP(slug: string) {
  const [msp, setMsp] = useState<DBMsp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirectSlug, setRedirectSlug] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      fetchMSP();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchMSP = async () => {
    try {
      setLoading(true);
      setRedirectSlug(null);

      const { data, error } = await supabase
        .from('msps_public')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setMsp(data as DBMsp | null);
      } else {
        const { data: aliasData } = await supabase
          .from('slug_aliases')
          .select('new_slug')
          .eq('old_slug', slug)
          .maybeSingle();

        if (aliasData?.new_slug) {
          setRedirectSlug(aliasData.new_slug);
        }
        setMsp(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  const converted = useMemo(() => (msp ? convertToMSP(msp) : null), [msp]);

  return { msp: converted, rawMsp: msp, loading, error, redirectSlug };
}
